import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Text,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FiCheck } from 'react-icons/fi'
import { LuListStart } from 'react-icons/lu'

import { selectVisibleInvoices } from '../domain/selectors'
import type { DashboardFilter } from '../domain/types'

import { ServiceBillingTable } from '../components/ServiceBillingTable'
import { ServiceBillingTabs } from '../components/ServiceBillingTabs'

import {
  ServiceBillingFilterPopover,
  type ServiceBillingStatusFilter,
} from '../components/ServiceBillingFilterPopover'

import { getServiceBillingInvoices } from '../../../../api/invoices.api'
import { useNavigate } from 'react-router-dom'
import type { ServiceBillingInvoice } from '@/components/invoices/invoice.types'

type SortBy = 'alphabetical' | 'date' | 'billingPeriod' | 'status'
type SortDir = 'desc' | 'asc'
const DASHBOARD_STATE_KEY = 'serviceBillingDashboardState'

type StoredDashboardState = {
  filter?: DashboardFilter
  searchTerm?: string
  sortBy?: SortBy
  sortDir?: SortDir
  appliedStatusFilter?: ServiceBillingStatusFilter
  appliedDateFrom?: string
  appliedDateTo?: string
  draftStatusFilter?: ServiceBillingStatusFilter
  draftDateFrom?: string
  draftDateTo?: string
}

function readDashboardState(): StoredDashboardState {
  try {
    const raw = sessionStorage.getItem(DASHBOARD_STATE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as StoredDashboardState
  } catch {
    return {}
  }
}

export function ServiceBillingDashboard() {
  const [invoices, setInvoices] = useState<ServiceBillingInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const initialDashboardState = readDashboardState()

  const [filter, setFilter] = useState<DashboardFilter>(() => {
    const value = initialDashboardState.filter
    return value === 'OPEN' || value === 'ARCHIVE' || value === 'ALL' ? value : 'OPEN'
  })

  const [searchTerm, setSearchTerm] = useState(() => initialDashboardState.searchTerm ?? '')

  const [sortBy, setSortBy] = useState<SortBy>(() => {
    const value = initialDashboardState.sortBy
    return value === 'alphabetical' ||
      value === 'date' ||
      value === 'billingPeriod' ||
      value === 'status'
      ? value
      : 'date'
  })

  const [sortDir, setSortDir] = useState<SortDir>(() => {
    const value = initialDashboardState.sortDir
    return value === 'asc' || value === 'desc' ? value : 'desc'
  })

  const navigate = useNavigate()

  const [appliedStatusFilter, setAppliedStatusFilter] = useState<ServiceBillingStatusFilter>(
    () => {
      return (
        initialDashboardState.appliedStatusFilter ?? {
          pendingApproval: true,
          approved: true,
          rejected: true,
        }
      )
    }
  )

  const [appliedDateFrom, setAppliedDateFrom] = useState(
    () => initialDashboardState.appliedDateFrom ?? ''
  )
  const [appliedDateTo, setAppliedDateTo] = useState(
    () => initialDashboardState.appliedDateTo ?? ''
  )

  const [draftStatusFilter, setDraftStatusFilter] = useState<ServiceBillingStatusFilter>(() => {
    return (
      initialDashboardState.draftStatusFilter ?? {
        pendingApproval: true,
        approved: true,
        rejected: true,
      }
    )
  })

  const [draftDateFrom, setDraftDateFrom] = useState(
    () => initialDashboardState.draftDateFrom ?? ''
  )
  const [draftDateTo, setDraftDateTo] = useState(() => initialDashboardState.draftDateTo ?? '')

  const loadInvoices = useCallback(async () => {
    try {
      setIsLoading(true)
      setLoadError(false)

      const data = await getServiceBillingInvoices({})
      setInvoices(data.items)
    } catch (error) {
      console.error('Failed to load invoices', error)
      setLoadError(true)
      setInvoices([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  useEffect(() => {
    if (filter === 'ALL') {
      const defaultFilter = {
        pendingApproval: true,
        approved: true,
        rejected: true,
      }

      setDraftStatusFilter(defaultFilter)
      setAppliedStatusFilter(defaultFilter)
    }
  }, [filter])

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DASHBOARD_STATE_KEY)
      if (!raw) return

      const saved = JSON.parse(raw)

      if (saved.filter === 'OPEN' || saved.filter === 'ARCHIVE' || saved.filter === 'ALL') {
        setFilter(saved.filter)
      }

      if (saved.searchTerm !== undefined) {
        setSearchTerm(saved.searchTerm)
      }

      if (
        saved.sortBy === 'alphabetical' ||
        saved.sortBy === 'date' ||
        saved.sortBy === 'billingPeriod' ||
        saved.sortBy === 'status'
      ) {
        setSortBy(saved.sortBy)
      }

      if (saved.sortDir === 'asc' || saved.sortDir === 'desc') {
        setSortDir(saved.sortDir)
      }

      if (saved.appliedStatusFilter) {
        setAppliedStatusFilter(saved.appliedStatusFilter)
      }

      if (saved.appliedDateFrom !== undefined) {
        setAppliedDateFrom(saved.appliedDateFrom)
      }

      if (saved.appliedDateTo !== undefined) {
        setAppliedDateTo(saved.appliedDateTo)
      }

      if (saved.draftStatusFilter) {
        setDraftStatusFilter(saved.draftStatusFilter)
      }

      if (saved.draftDateFrom !== undefined) {
        setDraftDateFrom(saved.draftDateFrom)
      }

      if (saved.draftDateTo !== undefined) {
        setDraftDateTo(saved.draftDateTo)
      }
    } catch (error) {
      console.error('Failed to restore dashboard state', error)
    }
  }, [])

  useEffect(() => {
    sessionStorage.setItem(
      DASHBOARD_STATE_KEY,
      JSON.stringify({
        filter,
        searchTerm,
        sortBy,
        sortDir,
        appliedStatusFilter,
        appliedDateFrom,
        appliedDateTo,
        draftStatusFilter,
        draftDateFrom,
        draftDateTo,
      })
    )
  }, [
    filter,
    searchTerm,
    sortBy,
    sortDir,
    appliedStatusFilter,
    appliedDateFrom,
    appliedDateTo,
    draftStatusFilter,
    draftDateFrom,
    draftDateTo,
  ])

  function matchesDateFilter(invoice: ServiceBillingInvoice) {
    const invoiceDate = new Date(invoice.sentAt ?? invoice.finalizedAt ?? invoice.createdAt)
    invoiceDate.setHours(0, 0, 0, 0)

    if (appliedDateFrom) {
      const from = new Date(appliedDateFrom)
      from.setHours(0, 0, 0, 0)
      if (invoiceDate < from) return false
    }

    if (appliedDateTo) {
      const to = new Date(appliedDateTo)
      to.setHours(0, 0, 0, 0)
      if (invoiceDate > to) return false
    }

    return true
  }

  const visibleInvoices = useMemo(() => {
    const result = selectVisibleInvoices(invoices, filter, searchTerm)
      .filter(matchesStatusFilter)
      .filter(matchesDateFilter)

    const sorted = [...result].sort((a, b) => {
      const direction = sortDir === 'asc' ? 1 : -1

      const getDate = (inv: ServiceBillingInvoice) =>
        new Date(inv.sentAt ?? inv.finalizedAt ?? inv.createdAt).getTime()

      const getBillingPeriodDate = (inv: ServiceBillingInvoice) =>
        new Date(inv.periodStart ?? inv.createdAt).getTime()

      const getAlphabeticalValue = (inv: ServiceBillingInvoice) =>
        (inv.consultantName ?? '').toLocaleLowerCase('de')

      const getStatusValue = (inv: ServiceBillingInvoice) =>
        `${inv.approvalStatus ?? ''} ${inv.status ?? ''}`

      switch (sortBy) {
        case 'alphabetical':
          return getAlphabeticalValue(a).localeCompare(getAlphabeticalValue(b), 'de') * direction

        case 'date':
          return (getDate(a) - getDate(b)) * direction

        case 'billingPeriod':
          return (getBillingPeriodDate(a) - getBillingPeriodDate(b)) * direction

        case 'status':
          return getStatusValue(a).localeCompare(getStatusValue(b), 'de') * direction

        default:
          return 0
      }
    })

    return sorted
  }, [
    invoices,
    filter,
    searchTerm,
    sortBy,
    sortDir,
    appliedStatusFilter,
    appliedDateFrom,
    appliedDateTo,
  ])

  // Applied popover filters (date range / narrowed statuses) — drives the solid
  // "Filter" button state, mirroring the Bestellübersicht toolbar.
  const hasActiveFilterPopoverFilter = useMemo(() => {
    if (appliedDateFrom || appliedDateTo) return true
    // status filter only applies on ALL/ARCHIVE; treat "not all selected" as active
    if (filter === 'ALL' || filter === 'ARCHIVE') {
      const { pendingApproval, approved, rejected } = appliedStatusFilter
      if (!(pendingApproval && approved && rejected)) return true
    }
    return false
  }, [appliedDateFrom, appliedDateTo, appliedStatusFilter, filter])

  const isFiltered = useMemo(
    () => Boolean(searchTerm.trim()) || hasActiveFilterPopoverFilter,
    [searchTerm, hasActiveFilterPopoverFilter]
  )

  function matchesStatusFilter(invoice: ServiceBillingInvoice) {
    if (filter !== 'ALL' && filter !== 'ARCHIVE') {
      return true
    }

    // The status checkboxes only narrow the list: hide a category when it is
    // explicitly unchecked. With everything checked (the default) this is a no-op,
    // so each tab shows exactly what its own definition (matchesFilter) intends.
    // Invoices outside these approval categories (e.g. canceled, or SIB self-billed
    // with approvalStatus "NA") must not be dropped here.
    if (invoice.approvalStatus === 'PENDING_APPROVAL' && !appliedStatusFilter.pendingApproval) {
      return false
    }

    if (invoice.approvalStatus === 'APPROVED' && !appliedStatusFilter.approved) {
      return false
    }

    if (invoice.approvalStatus === 'REJECTED' && !appliedStatusFilter.rejected) {
      return false
    }

    return true
  }

  return (
    <Box data-testid="invoice-verification-widget" w="full" p={4}>
      <Box w="full">
        <Heading size="lg" fontWeight="normal" mb={4}>
          Rechnungsprüfung
        </Heading>

        <Flex mb={4} gap={3} align="center">
          <HStack w="full" gap={3} align="center" flexWrap="wrap">
            {/* Search */}
            <Input
              flex="1"
              minW="240px"
              placeholder="Einträge durchsuchen"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />

            {/* Tabs (Open / Archive / All) */}
            <ServiceBillingTabs value={filter} onChange={setFilter} />

            {/* Sorting */}
            <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
              <MenuTrigger asChild>
                <Button variant="subtle" size="sm">
                  <HStack gap={2}>
                    <LuListStart />
                    <Text>Sortierung</Text>
                  </HStack>
                </Button>
              </MenuTrigger>

              <MenuPositioner>
                <MenuContent minW="260px">
                  <MenuItem value="sort:alphabetical" onClick={() => setSortBy('alphabetical')}>
                    <HStack w="full" justify="space-between">
                      <Text>Alphabetisch</Text>
                      {sortBy === 'alphabetical' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>

                  <MenuItem value="sort:date" onClick={() => setSortBy('date')}>
                    <HStack w="full" justify="space-between">
                      <Text>Datum</Text>
                      {sortBy === 'date' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>

                  <MenuItem value="sort:billingPeriod" onClick={() => setSortBy('billingPeriod')}>
                    <HStack w="full" justify="space-between">
                      <Text>Abrechnungszeitraum</Text>
                      {sortBy === 'billingPeriod' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>

                  <MenuItem value="sort:status" onClick={() => setSortBy('status')}>
                    <HStack w="full" justify="space-between">
                      <Text>Status</Text>
                      {sortBy === 'status' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>

                  <MenuSeparator />

                  <MenuItem value="dir:desc" onClick={() => setSortDir('desc')}>
                    <HStack w="full" justify="space-between">
                      <Text>Absteigend</Text>
                      {sortDir === 'desc' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>

                  <MenuItem value="dir:asc" onClick={() => setSortDir('asc')}>
                    <HStack w="full" justify="space-between">
                      <Text>Aufsteigend</Text>
                      {sortDir === 'asc' ? <Icon as={FiCheck} /> : null}
                    </HStack>
                  </MenuItem>
                </MenuContent>
              </MenuPositioner>
            </MenuRoot>

            <ServiceBillingFilterPopover
              filter={filter}
              draftStatusFilter={draftStatusFilter}
              draftDateFrom={draftDateFrom}
              draftDateTo={draftDateTo}
              hasActiveFilter={hasActiveFilterPopoverFilter}
              onChangeStatusFilter={setDraftStatusFilter}
              onChangeDateFrom={setDraftDateFrom}
              onChangeDateTo={setDraftDateTo}
              onApply={() => {
                setAppliedStatusFilter(draftStatusFilter)
                setAppliedDateFrom(draftDateFrom)
                setAppliedDateTo(draftDateTo)
              }}
            />
          </HStack>
        </Flex>

        <ServiceBillingTable
          rows={visibleInvoices}
          isLoading={isLoading}
          error={loadError}
          onRetry={loadInvoices}
          isFiltered={isFiltered}
          filter={filter}
          onRowClick={invoice => {
            // Every invoice is openable (read-only for already-processed ones).
            navigate(`/app/manager/service-billing/invoices/${invoice.id}`, {
              state: { invoice },
            })
          }}
        />
      </Box>
    </Box>
  )
}
